import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { AlertTriangle, Package, TrendingDown, TrendingUp, Download } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { supabase } from "@/lib/supabase";
import { useToast } from "@/components/ui/use-toast";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import * as XLSX from 'xlsx';

interface Product {
  id: number;
  code: string;
  name: string;
  categories: {
    id: number;
    name: string;
  };
}

interface Department {
  id: number;
  name: string;
  sigla: string;
}

interface InventoryItem {
  id: number;
  quantity: number;
  initial_quantity: number;
  department_id: number;
  departments: Department;
  products: Product;
}

interface TransformedProduct {
  product_id: number;
  code: string;
  product_name: string;
  category_name: string;
  department_id: number;
  department_name: string;
  department_sigla: string;
  quantity: number;
  min_quantity: number;
  status: string;
}

interface StockStats {
  total_products: number;
  low_stock: number;
  monthly_in: number;
  monthly_out: number;
}

const Stock = () => {
  const { toast } = useToast();
  const [products, setProducts] = useState<TransformedProduct[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [categories, setCategories] = useState<{ id: number; name: string }[]>([]);
  const [selectedDepartment, setSelectedDepartment] = useState<string>("all");
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const [selectedStatus, setSelectedStatus] = useState<string>("all");
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const itemsPerPage = 10;
  const [stats, setStats] = useState<StockStats>({
    total_products: 0,
    low_stock: 0,
    monthly_in: 0,
    monthly_out: 0,
  });

  useEffect(() => {
    fetchDepartments();
    fetchCategories();
    fetchProducts();
    fetchStats();
  }, []);

  useEffect(() => {
    fetchProducts();
    fetchStats();
  }, [selectedDepartment, selectedCategory, selectedStatus, searchQuery, currentPage]);

  const fetchDepartments = async () => {
    try {
      const { data, error } = await supabase
        .from("departments")
        .select("id, name, sigla")
        .order("sigla", { ascending: true });

      if (error) throw error;
      setDepartments(data || []);

      // Se houver apenas um departamento, seleciona automaticamente
      if (data && data.length === 1) {
        setSelectedDepartment(data[0].id.toString());
      }
    } catch (error) {
      console.error("Error fetching departments:", error);
      toast({
        title: "Erro",
        description: "Não foi possível carregar as secretarias",
        variant: "destructive",
      });
    }
  };

  const fetchCategories = async () => {
    try {
      const { data, error } = await supabase
        .from("categories")
        .select("id, name")
        .order("name", { ascending: true });

      if (error) throw error;
      setCategories(data || []);
    } catch (error) {
      console.error("Error fetching categories:", error);
      toast({
        title: "Erro",
        description: "Não foi possível carregar as categorias",
        variant: "destructive",
      });
    }
  };

  const fetchProducts = async () => {
    try {
      console.log("Fetching with filters:", {
        department: selectedDepartment,
        category: selectedCategory,
        status: selectedStatus,
        search: searchQuery
      });

      // Base query
      let baseQuery = supabase
        .from("products")
        .select(`
          id,
          code,
          name,
          categories!inner (
            id,
            name
          ),
          inventory!inner (
            quantity,
            initial_quantity,
            department_id,
            departments!inner (
              id,
              name,
              sigla
            )
          )
        `, { count: 'exact' });

      // Apply filters
      if (selectedDepartment && selectedDepartment !== "all") {
        baseQuery = baseQuery.eq("inventory.department_id", selectedDepartment);
      }

      if (selectedCategory && selectedCategory !== "all") {
        baseQuery = baseQuery.eq("categories.id", selectedCategory);
      }

      if (selectedStatus && selectedStatus !== "all") {
        if (selectedStatus === "low") {
          baseQuery = baseQuery.lt("inventory.quantity", "inventory.initial_quantity * 0.8");
        } else if (selectedStatus === "normal") {
          baseQuery = baseQuery.gte("inventory.quantity", "inventory.initial_quantity * 0.8");
        }
      }

      if (searchQuery) {
        baseQuery = baseQuery.ilike("name", `%${searchQuery}%`);
      }

      // Get paginated data with count
      const { data, error, count } = await baseQuery
        .order('name')
        .range((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage - 1);

      console.log("Raw data:", data);
      console.log("Total count:", count);
      console.log("Error:", error);

      if (error) throw error;

      if (!data) {
        setProducts([]);
        setTotalPages(1);
        return;
      }

      const transformedProducts = data.map((item: any): TransformedProduct => {
        const inventory = Array.isArray(item.inventory) ? item.inventory[0] : item.inventory;
        const department = inventory?.departments || {};

        return {
          product_id: item.id,
          code: item.code || '',
          product_name: item.name || '',
          category_name: item.categories?.name || "Sem categoria",
          department_id: inventory?.department_id || 0,
          department_name: department?.name || "",
          department_sigla: department?.sigla || "",
          quantity: inventory?.quantity || 0,
          min_quantity: Math.ceil((inventory?.initial_quantity || 0) * 0.8),
          status: getStockStatus(
            inventory?.quantity || 0, 
            Math.ceil((inventory?.initial_quantity || 0) * 0.8)
          )
        };
      });

      console.log("Transformed products:", transformedProducts);
      setProducts(transformedProducts);
      setTotalPages(Math.ceil((count || 0) / itemsPerPage));
    } catch (error) {
      console.error("Error fetching products:", error);
      toast({
        title: "Erro",
        description: "Não foi possível carregar os produtos",
        variant: "destructive",
      });
    }
  };

  const getStockStatus = (current: number, minimum: number) => {
    return current < minimum ? "baixo" : "normal";
  };

  const fetchStats = async () => {
    try {
      let query = supabase
        .from("inventory")
        .select(`
          product_id,
          quantity,
          initial_quantity
        `);

      if (selectedDepartment !== "all") {
        query = query.eq("department_id", selectedDepartment);
      }

      const { data: productsData, error: productsError } = await query;

      if (productsError) throw productsError;

      // Calcula produtos com estoque baixo (menos de 80% do estoque inicial)
      const lowStockCount = productsData?.filter((p) =>
        p.quantity <= Math.ceil(p.initial_quantity * 0.8)
      ).length || 0;

      // Movimentações do mês
      const startOfMonth = new Date();
      startOfMonth.setDate(1);
      startOfMonth.setHours(0, 0, 0, 0);

      let transactionsQuery = supabase
        .from("transactions")
        .select("type, quantity")
        .gte("created_at", startOfMonth.toISOString());

      if (selectedDepartment !== "all") {
        transactionsQuery = transactionsQuery.eq("department_id", selectedDepartment);
      }

      const { data: transactionsData, error: transactionsError } = await transactionsQuery;

      if (transactionsError) throw transactionsError;

      // Contar apenas produtos únicos com inventário
      const uniqueProductIds = new Set();
      productsData?.forEach(item => {
        // Verificar se o produto tem um registro válido no inventário
        if (item.quantity !== null && item.quantity !== undefined) {
          // Adicionar o ID do produto ao conjunto de IDs únicos
          uniqueProductIds.add(item.product_id);
        }
      });
      
      setStats({
        total_products: uniqueProductIds.size,
        low_stock: lowStockCount,
        monthly_in: transactionsData?.filter((t) => t.type === "entrada").reduce((sum, t) => sum + t.quantity, 0) || 0,
        monthly_out: transactionsData?.filter((t) => t.type === "saida").reduce((sum, t) => sum + t.quantity, 0) || 0,
      });
    } catch (error) {
      toast({
        title: "Erro",
        description: "Não foi possível carregar as estatísticas",
        variant: "destructive",
      });
    }
  };

  const exportToExcel = async () => {
    try {
      const { data, error } = await supabase
        .from("products")
        .select(`
          id,
          code,
          name,
          categories!inner (
            id,
            name
          ),
          inventory!inner (
            quantity,
            initial_quantity,
            department_id,
            departments!inner (
              id,
              name,
              sigla
            )
          )
        `);

      if (error) throw error;

      const exportData = (data || []).map((item: any) => {
        const inventory = Array.isArray(item.inventory) ? item.inventory[0] : item.inventory;
        const department = inventory?.departments || {};

        return {
          'Código': item.code || '',
          'Nome': item.name || '',
          'Categoria': item.categories?.name || 'Sem categoria',
          'Secretaria': department?.sigla || '',
          'Quantidade': inventory?.quantity || 0,
          'Estoque Mínimo': Math.ceil((inventory?.initial_quantity || 0) * 0.8),
          'Status': getStockStatus(
            inventory?.quantity || 0, 
            Math.ceil((inventory?.initial_quantity || 0) * 0.8)
          )
        };
      });

      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Produtos");
      
      // Generate file name with current date
      const date = new Date().toISOString().split('T')[0];
      XLSX.writeFile(wb, `estoque_${date}.xlsx`);

      toast({
        title: "Sucesso",
        description: "Planilha exportada com sucesso!",
        variant: "default",
      });
    } catch (error) {
      console.error("Error exporting to Excel:", error);
      toast({
        title: "Erro",
        description: "Não foi possível exportar a planilha",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="container mx-auto py-6 space-y-4">
      <div className="grid gap-4 md:grid-cols-4">
        <Card className="bg-blue-50">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total de Produtos</CardTitle>
            <Package className="h-4 w-4 text-blue-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">{stats.total_products}</div>
          </CardContent>
        </Card>

        <Card className="bg-red-50">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Estoque Baixo</CardTitle>
            <AlertTriangle className="h-4 w-4 text-red-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">{stats.low_stock}</div>
          </CardContent>
        </Card>

        <Card className="bg-green-50">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Entradas no Mês</CardTitle>
            <TrendingUp className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{stats.monthly_in}</div>
          </CardContent>
        </Card>

        <Card className="bg-orange-50">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Saídas no Mês</CardTitle>
            <TrendingDown className="h-4 w-4 text-orange-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-600">{stats.monthly_out}</div>
          </CardContent>
        </Card>
      </div>

      <Card className="bg-white">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Filtros</CardTitle>
          <Button 
            onClick={exportToExcel}
            className="bg-green-600 hover:bg-green-700"
          >
            <Download className="mr-2 h-4 w-4" />
            Exportar Excel
          </Button>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-4">
            <div className="space-y-2">
              <label htmlFor="department">Secretaria</label>
              <Select
                value={selectedDepartment}
                onValueChange={setSelectedDepartment}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione uma secretaria" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas as Secretarias</SelectItem>
                  {departments.map((dept) => (
                    <SelectItem key={dept.id} value={dept.id.toString()}>
                      {dept.sigla || dept.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <label htmlFor="category">Categoria</label>
              <Select
                value={selectedCategory}
                onValueChange={setSelectedCategory}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione uma categoria" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas as Categorias</SelectItem>
                  {categories.map((category) => (
                    <SelectItem key={category.id} value={category.id.toString()}>
                      {category.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <label htmlFor="status">Status do Estoque</label>
              <Select
                value={selectedStatus}
                onValueChange={setSelectedStatus}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione o status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="low">Estoque Baixo</SelectItem>
                  <SelectItem value="normal">Estoque Normal</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <label htmlFor="search">Buscar Produto</label>
              <Input
                id="search"
                placeholder="Nome ou código do produto"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Produtos em Estoque</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Código</TableHead>
                  <TableHead>Nome</TableHead>
                  <TableHead>Categoria</TableHead>
                  <TableHead>Secretaria</TableHead>
                  <TableHead>Quantidade</TableHead>
                  <TableHead>Estoque Mínimo</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {products.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-4">
                      Nenhum produto encontrado
                    </TableCell>
                  </TableRow>
                ) : (
                  products.map((product) => (
                    <TableRow key={product.product_id}>
                      <TableCell>{product.code}</TableCell>
                      <TableCell>{product.product_name}</TableCell>
                      <TableCell>{product.category_name}</TableCell>
                      <TableCell>{product.department_sigla}</TableCell>
                      <TableCell>{product.quantity}</TableCell>
                      <TableCell>{product.min_quantity}</TableCell>
                      <TableCell>
                        <span
                          className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${
                            product.status === "baixo"
                              ? "bg-red-100 text-red-700"
                              : "bg-green-100 text-green-700"
                          }`}
                        >
                          {product.status === "baixo" ? "Baixo" : "Normal"}
                        </span>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          <div className="flex items-center justify-end space-x-2 py-4">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage((old) => Math.max(old - 1, 1))}
              disabled={currentPage === 1}
            >
              <ChevronLeft className="h-4 w-4" />
              Anterior
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage((old) => Math.min(old + 1, totalPages))}
              disabled={currentPage === totalPages}
            >
              Próximo
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Stock;